import React from 'react';

import htmldiff from 'htmldiff';
import {colors} from 'js-libs/sweetcommons/styles';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';

import {escapeHtml, markAsSafe} from 'app/utils/strings';
import {groupVersions} from 'app/version/utils';

export const HTMLDiffDiv = styled.div`
  ins {
    background: ${colors.insGreen};
    color: ${colors.insGreenText};

    img {
      border: 5px solid ${colors.insGreen};
    }
  }

  del {
    background: ${colors.delRed};
    color: ${colors.delRedText};

    img {
      border: 5px solid ${colors.delRed};
    }
  }
`;

export function getComparedVersion<
  T extends {
    id: number | string;
    url: string;
    html_url: string;
    modified_at: string;
    created_at: string;
    approved_at: string | null;
    is_current: boolean;
  }
>(versionURL: string, versions: Array<T>, selectedGroup?: string): T | void {
  const groups = groupVersions<T>(versions);

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];

    for (let v = 0; v < group.length; v++) {
      const version = group[v];
      const versionId = version.id.toString();

      if (versionURL === version.url) {
        if (v === 0 && selectedGroup === versionId) {
          const nextGroup = groups[i + 1];
          return nextGroup != null ? nextGroup[0] : undefined;
        } else {
          for (let j = 0; j < versions.length; j++) {
            if (versions[j].url === versionURL) {
              return versions[j + 1];
            }
          }
        }
      }
    }
  }
}

export function getHtmlDiff(oldTitle: string, newTitle: string, escape = true) {
  return oldTitle === newTitle ? (
    newTitle
  ) : (
    <HTMLDiffDiv
      dangerouslySetInnerHTML={markAsSafe(
        htmldiff(
          escape ? escapeHtml(oldTitle) : oldTitle,
          escape ? escapeHtml(newTitle) : newTitle
        ).replace(/&amp;/g, '&')
      )}
    />
  );
}

export function getListsDiff<T, R>(
  oldList: Array<T>,
  newList: Array<T>,
  idGetter: (newItem: T) => string | number,
  processInserted?: (newItem: T) => R,
  processChanged?: (oldItem: T, newItem: T) => R,
  processDeleted?: (oldItem: T) => R
): Array<T | R> {
  // changed/inserted ones
  const existing = newList.map<T | R>((item) => {
    const old = find(oldList, (i) => idGetter(i) === idGetter(item));

    if (processInserted && old == null) {
      return processInserted(item);
    }

    if (processChanged && old && !isEqual(old, item)) {
      return processChanged(old, item);
    }

    return item;
  });

  // deleted ones
  oldList &&
    oldList.forEach((old, index) => {
      if (
        processDeleted &&
        find(newList, (i) => idGetter(i) === idGetter(old)) == null
      ) {
        existing.splice(index, 0, processDeleted(old));
      }
    });

  return existing;
}
